import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { 
  GraduationCap, 
  Briefcase, 
  Plus, 
  Trash2,
  Globe,
  Save,
  CheckCircle,
  HelpCircle,
  AlertCircle
} from 'lucide-react'
import { UserProfileData, Qualification, CareerHistoryEntry, LanguageProficiency, QualificationType, QUALIFICATION_NQF_MAP } from '@/types/onboarding'
import QualificationForm from '@/components/profile/QualificationForm'
import toast from 'react-hot-toast'

interface ProfileCompletionStepProps {
  initialData?: Partial<UserProfileData>
  onSave: (data: Partial<UserProfileData>) => void
  onSaveDraft?: (data: Partial<UserProfileData>) => void
  onSkip?: () => void
  allowSkip?: boolean
  onBack: () => void
}

// Pure CSS tooltip — hover-only, no sticky behavior
function FieldTooltip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex ml-1 group/tip cursor-help">
      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 group-hover/tip:text-muted-foreground transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-[260px] rounded-lg bg-gray-900 px-3.5 py-2.5 text-xs leading-relaxed text-gray-100 shadow-lg opacity-0 scale-95 transition-all duration-150 group-hover/tip:opacity-100 group-hover/tip:scale-100 z-50">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  )
}

export default function ProfileCompletionStep({
  initialData,
  onSave,
  onSaveDraft,
  onSkip,
  allowSkip = false,
  onBack
}: ProfileCompletionStepProps) {
  const [qualifications, setQualifications] = useState<Qualification[]>(initialData?.qualifications || [])
  const [careerHistory, setCareerHistory] = useState<CareerHistoryEntry[]>(initialData?.careerHistory || [])
  const [areasOfExpertise, setAreasOfExpertise] = useState<string[]>(initialData?.professionalContext?.areasOfExpertise || [])
  const [languages] = useState<LanguageProficiency[]>(initialData?.professionalContext?.languageProficiencies || [])
  const [newExpertise, setNewExpertise] = useState('')
  const [hasUserEditedExpertise, setHasUserEditedExpertise] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  
  // Sync local state when initialData changes (e.g., when navigating back to this step)
  // Only sync if user hasn't made local edits
  useEffect(() => {
    if (initialData) {
      if (initialData.qualifications?.length) {
        setQualifications(initialData.qualifications)
      }
      if (initialData.careerHistory?.length) {
        setCareerHistory(initialData.careerHistory)
      }
      // Only sync expertise if user hasn't made local edits
      if (!hasUserEditedExpertise && initialData.professionalContext?.areasOfExpertise?.length) {
        setAreasOfExpertise(initialData.professionalContext.areasOfExpertise)
      }
    }
  }, [initialData, hasUserEditedExpertise])

  // Add new qualification with NQF-aligned structure
  const addQualification = (type: QualificationType = 'bachelors') => {
    const nqf = QUALIFICATION_NQF_MAP[type as keyof typeof QUALIFICATION_NQF_MAP]
    const newQual: Qualification = {
      id: `qual-${Date.now()}`,
      type: type,
      institution: '',
      country: type === 'international' ? 'international' : 'south_africa',
      inProgress: false,
      ...(nqf !== undefined && { nqfLevel: nqf }),
    }
    setQualifications([...qualifications, newQual])
    toast.success('Qualification added')
  }

  // Update a specific qualification
  const updateQualification = (id: string, updatedQual: Qualification) => {
    setQualifications(prev => prev.map(q => q.id === id ? updatedQual : q))
  }

  // Remove a qualification
  const removeQualification = (id: string) => {
    setQualifications(prev => prev.filter(q => q.id !== id))
    toast.success('Qualification removed')
  }

  // Add new career entry
  const addCareerEntry = () => {
    const newEntry: CareerHistoryEntry = {
      id: `career-${Date.now()}`,
      organization: '',
      position: '',
      startDate: '',
      endDate: '',
      directReports: 0,
      extendedTeamSize: 0,
      keyAchievements: [],
      skillsDeveloped: []
    }
    setCareerHistory([...careerHistory, newEntry])
    toast.success('Career entry added')
  }

  // Validate all added items have their required fields filled
  const validateProfile = (): string[] => {
    const errors: string[] = []

    qualifications.forEach((q, i) => {
      // Institution required for academic qualifications only (not professional certs)
      if (q.type !== 'professional' && !q.institution?.trim()) {
        errors.push(`Qualification ${i + 1}: Institution is required`)
      }
      // Qualification Name required for all academic types except Grade 12 (NSC)
      if (q.type !== 'nsc' && q.type !== 'professional' && !q.qualificationName?.trim()) {
        errors.push(`Qualification ${i + 1}: Qualification Name is required`)
      }
      // Professional certs require professional body
      if (q.type === 'professional' && !q.professionalBody?.trim()) {
        errors.push(`Qualification ${i + 1}: Professional Body is required for professional certifications`)
      }
    })

    careerHistory.forEach((c, i) => {
      if (!c.organization?.trim()) {
        errors.push(`Career Entry ${i + 1}: Organisation is required`)
      }
      if (!c.position?.trim()) {
        errors.push(`Career Entry ${i + 1}: Position is required`)
      }
      if (!c.startDate) {
        errors.push(`Career Entry ${i + 1}: Start Date is required`)
      }
      if (!c.endDate) {
        errors.push(`Career Entry ${i + 1}: End Date is required`)
      }
    })

    return errors
  }

  const handleSave = () => {
    const errors = validateProfile()
    setValidationErrors(errors)
    if (errors.length > 0) {
      toast.error(errors.length === 1 ? errors[0] : `${errors.length} required fields are missing — see details above`)
      return
    }

    const profileData: Partial<UserProfileData> = {
      qualifications,
      careerHistory,
      professionalContext: {
        areasOfExpertise,
        languageProficiencies: languages,
        geographicExperience: [],
        industryExperience: []
      },
      completionStatus: 'complete',
      lastUpdated: new Date().toISOString()
    }
    onSave(profileData)
  }

  const handleSaveDraft = async () => {
    const profileData: Partial<UserProfileData> = {
      qualifications,
      careerHistory,
      professionalContext: {
        areasOfExpertise,
        languageProficiencies: languages,
        geographicExperience: [],
        industryExperience: []
      },
      completionStatus: 'partial',
      lastUpdated: new Date().toISOString()
    }

    if (onSaveDraft) {
      try {
        await onSaveDraft(profileData)
        toast.success('Progress saved!')
      } catch (error) {
        // Don't show a duplicate toast — the apiClient interceptor already
        // shows "Server error" for 500 responses. Only show for non-HTTP errors.
        const isHttpError = error && typeof error === 'object' && 'response' in error
        if (!isHttpError) {
          toast.error('Failed to save progress. Please try again.')
        }
      }
    }
  }

  // Summary counts for the progress indicator
  const qualCount = qualifications.length
  const careerCount = careerHistory.length
  const expertiseCount = areasOfExpertise.length

  return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-2"
        >
          <h2 className="text-3xl font-larken font-bold text-heritage-blue">
            Tell Us About Yourself
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            This helps your people leader understand your background and supports meaningful career development conversations.
          </p>
        </motion.div>

        {/* Progress Summary */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center gap-4"
        >
          <Badge variant="outline" className={`px-3 py-1.5 ${qualCount > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-muted/50'}`}>
            <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
            {qualCount} {qualCount === 1 ? 'Qualification' : 'Qualifications'}
          </Badge>
          <Badge variant="outline" className={`px-3 py-1.5 ${careerCount > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-muted/50'}`}>
            <Briefcase className="h-3.5 w-3.5 mr-1.5" />
            {careerCount} {careerCount === 1 ? 'Role' : 'Roles'}
          </Badge>
          <Badge variant="outline" className={`px-3 py-1.5 ${expertiseCount > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-muted/50'}`}>
            <Globe className="h-3.5 w-3.5 mr-1.5" />
            {expertiseCount} {expertiseCount === 1 ? 'Expertise' : 'Expertise Areas'}
          </Badge>
        </motion.div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-900">Please fix the following:</h3>
                <ul className="text-sm text-red-800 mt-2 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* ─── Section 1: Qualifications ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <GraduationCap className="h-5 w-5 text-heritage-blue" />
                  <span>Qualifications & Certifications</span>
                  <FieldTooltip text="Add your academic degrees, professional certifications, and international credentials. Qualifications are aligned to the SA National Qualifications Framework (NQF levels 1-10)." />
                </CardTitle>
                <div className="flex gap-2">
                  <Button onClick={() => addQualification('bachelors')} size="sm" variant="outline">
                    <Plus className="mr-1.5 h-4 w-4" />
                    Degree
                  </Button>
                  <Button onClick={() => addQualification('professional')} size="sm" variant="outline">
                    <Plus className="mr-1.5 h-4 w-4" />
                    Professional Cert
                  </Button>
                  <Button onClick={() => addQualification('international')} size="sm" variant="outline">
                    <Plus className="mr-1.5 h-4 w-4" />
                    International
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {qualifications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GraduationCap className="mx-auto h-12 w-12 mb-2 opacity-20" />
                  <p className="text-sm">No qualifications added yet</p>
                  <p className="text-xs mt-1">Use the buttons above to add your qualifications</p>
                </div>
              ) : (
                qualifications.map((qual, index) => (
                  <QualificationForm
                    key={qual.id}
                    qualification={qual}
                    index={index}
                    onChange={(updated) => updateQualification(qual.id, updated)}
                    onRemove={() => removeQualification(qual.id)}
                    disabled={false}
                    showAdvancedFields={false}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* ─── Section 2: Career History ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Briefcase className="h-5 w-5 text-heritage-blue" />
                    <span>External Career History</span>
                    <FieldTooltip text="Your current Eduvos role is automatically included from F&O. Add any previous roles outside Eduvos here." />
                  </CardTitle>
                </div>
                <Button onClick={addCareerEntry} size="sm">
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add Previous Role
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {careerHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Briefcase className="mx-auto h-12 w-12 mb-2 opacity-20" />
                  <p className="text-sm">No external career history added yet</p>
                  <p className="text-xs mt-1">This is optional — add roles that are relevant to your career development</p>
                </div>
              ) : (
                careerHistory.map((entry) => (
                  <Card key={entry.id} className="bg-muted/30">
                    <CardContent className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Organisation <span className="text-red-500">*</span></Label>
                          <Input
                            value={entry.organization}
                            onChange={(e) => {
                              setCareerHistory(prev => prev.map(item => 
                                item.id === entry.id ? { ...item, organization: e.target.value } : item
                              ))
                            }}
                            className="h-9"
                            placeholder="Company name"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Position <span className="text-red-500">*</span></Label>
                          <Input
                            value={entry.position}
                            onChange={(e) => {
                              setCareerHistory(prev => prev.map(item => 
                                item.id === entry.id ? { ...item, position: e.target.value } : item
                              ))
                            }}
                            className="h-9"
                            placeholder="Job title"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3 items-end">
                        <div>
                          <Label className="text-xs">Start Date <span className="text-red-500">*</span></Label>
                          <Input
                            type="month"
                            value={entry.startDate}
                            onChange={(e) => {
                              setCareerHistory(prev => prev.map(item => 
                                item.id === entry.id ? { ...item, startDate: e.target.value } : item
                              ))
                            }}
                            required
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">End Date <span className="text-red-500">*</span></Label>
                          <Input
                            type="month"
                            value={entry.endDate || ''}
                            onChange={(e) => {
                              setCareerHistory(prev => prev.map(item => 
                                item.id === entry.id ? { ...item, endDate: e.target.value } : item
                              ))
                            }}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs flex items-center">
                            Direct Reports
                            <FieldTooltip text="People who reported directly to you in this role" />
                          </Label>
                          <Input
                            type="number"
                            value={entry.directReports || ''}
                            onChange={(e) => {
                              setCareerHistory(prev => prev.map(item => 
                                item.id === entry.id ? { ...item, directReports: parseInt(e.target.value) || undefined } : item
                              ))
                            }}
                            className="h-9"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label className="text-xs flex items-center">
                            Extended Team
                            <FieldTooltip text="Total team size including indirect reports — e.g. if you managed 3 team leads who each had 5 people, that's 18" />
                          </Label>
                          <Input
                            type="number"
                            value={entry.extendedTeamSize || ''}
                            onChange={(e) => {
                              setCareerHistory(prev => prev.map(item => 
                                item.id === entry.id ? { ...item, extendedTeamSize: parseInt(e.target.value) || undefined } : item
                              ))
                            }}
                            className="h-9"
                            placeholder="0"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCareerHistory(prev => prev.filter(item => item.id !== entry.id))}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* ─── Section 3: Professional Context ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="h-5 w-5 text-heritage-blue" />
                <span>Professional Context</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="flex items-center">
                  Areas of Expertise
                  <FieldTooltip text="Skills and knowledge domains where you have significant experience — e.g. curriculum design, data analytics, student counselling" />
                </Label>
                <div className="flex space-x-2 mt-2">
                  <Input
                    value={newExpertise}
                    onChange={(e) => setNewExpertise(e.target.value)}
                    placeholder="Type an area of expertise and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newExpertise.trim()) {
                        e.preventDefault()
                        setHasUserEditedExpertise(true)
                        setAreasOfExpertise(prev => [...prev, newExpertise.trim()])
                        setNewExpertise('')
                      }
                    }}
                  />
                  <Button
                    type="button"
                    size="icon"
                    onClick={() => {
                      if (newExpertise.trim()) {
                        setHasUserEditedExpertise(true)
                        setAreasOfExpertise(prev => [...prev, newExpertise.trim()])
                        setNewExpertise('')
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {areasOfExpertise.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {areasOfExpertise.map((area, idx) => (
                      <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1 px-3 py-1.5">
                        {area}
                        <button
                          type="button"
                          onClick={() => {
                            setHasUserEditedExpertise(true)
                            setAreasOfExpertise(prev => prev.filter((_, i) => i !== idx))
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-4">
          <Button onClick={onBack} variant="outline">
            Back
          </Button>
          <div className="flex space-x-2">
            {allowSkip && onSkip && (
              <Button onClick={onSkip} variant="ghost">
                Skip for Now
              </Button>
            )}
            
            {/* Save for Later - saves without continuing */}
            <Button 
              onClick={handleSaveDraft} 
              variant="outline"
            >
              <Save className="mr-2 h-4 w-4" />
              Save for Later
            </Button>
            
            {/* Complete & Continue */}
            <Button 
              onClick={handleSave} 
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Complete & Continue
            </Button>
          </div>
        </div>
        
        {/* Helper text */}
        <p className="text-center text-sm text-muted-foreground">
          Sections are optional, but any items you add must have the required fields (marked with <span className="text-red-500">*</span>) filled in before continuing.
        </p>
      </div>
  )
}
